import {
  workOrderListQuery,
  WorkOrdersQueryListItem,
} from "@/app/ProDrivers/WorkOrders/GraphQL/workOrdersQuery";
import {
  DataTable,
  DataTableColDef,
  DataTableColumnHeader,
} from "@/lib/Components/DataTable/DataTable";
import { ModelLinkCell } from "@/lib/Components/DataTable/Cells/ModelLinkCell";
import { useWorkOrderActions } from "@/app/ProDrivers/WorkOrders/Hooks/useWorkOrderActions";
import { WorkOrderStatus } from "@/gql/graphql";
import { useGlobalSupplier } from "@/app/Suppliers/Utils/useGlobalSupplier";
import {
  MultiSelectFilter,
  MultiSelectFilterOption,
} from "@/lib/Components/DataTable/Filters/MultiSelectFilter";
import { Button } from "@/components/catalyst/button";
import { Badge } from "@/components/catalyst/badge";
import { workOrderStatusMap } from "@/app/ProDrivers/WorkOrders/utils/workOrderStatusMap";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { CreateWorkOrderDialog } from "@/app/ProDrivers/WorkOrders/Components/CreateWorkOrderDialog";
import { useTranslations } from "use-intl";
import { MessageSquareQuoteIcon } from "lucide-react";
import { TimeSinceNowCell } from "@/lib/Components/DataTable/TimeSinceNowCell";
import { getWorkOrderQueryVariables } from "@/app/ProDrivers/WorkOrders/utils/getWorkOrderQueryVariables";
import { CellContext } from "@/lib/Components/DataTable/types";
import { DeEmphasisedCell } from "@/lib/Components/DataTable/DeEmphasisedCell";
import { Avatar } from "@/components/catalyst/avatar";
import { getInitials } from "@/lib/Utils/getInitials";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import { formatDate } from "@/lib/Formatters/formatDate";
import { Highlighter } from "@/lib/Components/Highlighter";
import { ProDriverFilter } from "@/lib/Components/DataTable/Filters/ProDriverFilter";
import { SupplierFilter } from "@/lib/Components/DataTable/Filters/SupplierFilter";
import { useState } from "react";

export type WorkOrderTableCols =
  | "id"
  | "status"
  | "supplier"
  | "quote_amount"
  | "drivers"
  | "vehicle"
  | "from"
  | "to"
  | "depart_after"
  | "created_at";

function useWorkOrderFilters() {
  const t = useTranslations("workOrder.status");

  return [
    {
      label: t(WorkOrderStatus.QuoteRequested),
      value: WorkOrderStatus.QuoteRequested,
    },
    {
      label: t(WorkOrderStatus.QuoteIssued),
      value: WorkOrderStatus.QuoteIssued,
    },
    {
      label: t(WorkOrderStatus.AssigningDrivers),
      value: WorkOrderStatus.AssigningDrivers,
    },
    {
      label: t(WorkOrderStatus.DriversAssigned),
      value: WorkOrderStatus.DriversAssigned,
    },
    {
      label: t(WorkOrderStatus.InProgress),
      value: WorkOrderStatus.InProgress,
    },
    {
      label: t(WorkOrderStatus.Invoiced),
      value: WorkOrderStatus.Invoiced,
    },
    {
      label: t(WorkOrderStatus.Completed),
      value: WorkOrderStatus.Completed,
    },
    {
      label: t(WorkOrderStatus.QuoteRejected),
      value: WorkOrderStatus.QuoteRejected,
    },
    {
      label: t(WorkOrderStatus.Archived),
      value: WorkOrderStatus.Archived,
    },
  ] satisfies MultiSelectFilterOption<WorkOrderStatus>[];
}

export function WorkOrderTable({
  status,
  setStatus,
  relocationId,
}: {
  status: WorkOrderStatus[];
  setStatus: (status: WorkOrderStatus[]) => void;
  relocationId?: string;
}) {
  "use no memo";
  const t = useTranslations("workOrder");
  const tVehicle = useTranslations("vehicle");
  const getActions = useWorkOrderActions();
  const { supplierId: globalSupplierId } = useGlobalSupplier();
  const workOrderFilters = useWorkOrderFilters();
  const { open } = useDialog(CreateWorkOrderDialog);
  const [proDriverIds, setProDriverIds] = useState<string[]>([]);
  const [filterSupplierId, setFilterSupplierId] = useState<string | null>(null);

  const effectiveSupplierId = globalSupplierId ?? filterSupplierId;
  const activeFilterCount = [
    status.length > 0,
    proDriverIds.length > 0,
    filterSupplierId !== null,
  ].filter(Boolean).length;

  const defaultColumns: DataTableColDef<
    WorkOrdersQueryListItem,
    any,
    WorkOrderTableCols
  >[] = [
    {
      id: "id",
      accessorFn: (data) => data.id,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("workOrder")} />
      ),
      cell: LinkCell,
    },
    {
      id: "status",
      accessorFn: (data) => data,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("filters.status")} />
      ),
      cell: ({ row }) => {
        return (
          <Badge color={workOrderStatusMap[row.original.status]}>
            {t(`status.${row.original.status}`)}
          </Badge>
        );
      },
    },
    {
      id: "supplier",
      accessorFn: (data) => data.supplier.name,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("supplier")} />
      ),
      cell: SupplierCell,
    },
    {
      id: "from",
      accessorFn: (data) => data.departureAddress.city,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("from")} />
      ),
      cell: FromCell,
    },
    {
      id: "to",
      accessorFn: (data) => data.deliveryAddress.city,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("to")} />
      ),
      cell: ToCell,
    },
    {
      id: "depart_after",
      accessorFn: (data) => formatDate(data.depart_after),
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Depart" />
      ),
      cell: DeEmphasisedCell,
    },
    {
      id: "vehicle",
      accessorFn: (data) => tVehicle(`type.${data.vehicle_type}`),
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("vehicle")} />
      ),
      cell: DeEmphasisedCell,
    },
    {
      id: "quote_amount",
      accessorFn: (data) =>
        data.currentQuote
          ? formatCurrency(
              data.currentQuote.amount_exc_tax,
              data.currentQuote.currency,
            )
          : "--",
      header: "Amount",
      cell: DeEmphasisedCell,
    },
    {
      id: "drivers",
      accessorFn: (data) => data,
      header: "Drivers",
      cell: DriversCell,
    },
    {
      id: "created_at",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("created")} />
      ),

      accessorFn: (data) => data.created_at,
      cell: TimeSinceNowCell,
    },
  ];
  return (
    <DataTable
      id={"work-order-table"}
      initialSorting={[{ id: "id", desc: true }]}
      searchable
      rightButtons={
        <Button
          color="indigo"
          LeadingIcon={MessageSquareQuoteIcon}
          onClick={() => {
            open({});
          }}
        >
          {t("requestQuote")}
        </Button>
      }
      getActions={getActions}
      accessor={(data) => data.workOrders}
      columns={defaultColumns}
      document={workOrderListQuery}
      activeFilterCount={activeFilterCount}
      onClearFilters={() => {
        setStatus([]);
        setProDriverIds([]);
        setFilterSupplierId(null);
      }}
      filters={(table) => (
        <div className="flex flex-wrap gap-2">
          <MultiSelectFilter
            label={t("filters.status")}
            options={workOrderFilters}
            selected={status}
            onChange={(newStatus) => {
              setStatus(newStatus);
              table.setPagination((prev) => ({
                ...prev,
                pageIndex: 0,
              }));
            }}
          />

          {!table.options.meta?.search ? (
            <ProDriverFilter
              placeholder={t("filters.proDriver")}
              selected={proDriverIds}
              onChange={(newIds) => {
                setProDriverIds(newIds);
                table.setPagination((prev) => ({
                  ...prev,
                  pageIndex: 0,
                }));
              }}
            />
          ) : null}

          {!globalSupplierId ? (
            <SupplierFilter
              label={t("supplier")}
              selected={filterSupplierId}
              onChange={(value) => {
                setFilterSupplierId(value);
                table.setPagination((prev) => ({
                  ...prev,
                  pageIndex: 0,
                }));
              }}
            />
          ) : null}
        </div>
      )}
      getQueryVariables={getWorkOrderQueryVariables({
        supplierId: effectiveSupplierId,
        status,
        relocationId,
        proDriverIds,
      })}
    />
  );
}

function LinkCell({ row, table }: CellContext<WorkOrdersQueryListItem, any>) {
  "use no memo";
  const search = table.options.meta?.search ?? "";
  return (
    <ModelLinkCell
      viewTransition
      from="/$tenant/$orgId/supplier/{-$supplierId}"
      to="/$tenant/$orgId/supplier/{-$supplierId}/pro-drivers/work-orders/$id"
      params={(prev) => ({
        ...prev,
        id: row.original.id,
      })}
    >
      <Highlighter searchString={search}>{row.original.reference}</Highlighter>
    </ModelLinkCell>
  );
}

function SupplierCell({
  row,
  table,
}: CellContext<WorkOrdersQueryListItem, any>) {
  "use no memo";
  const search = table.options.meta?.search ?? "";
  return (
    <div className="text-gray-500">
      <Highlighter searchString={search}>
        {row.original.supplier.name}
      </Highlighter>
    </div>
  );
}

function FromCell({ row, table }: CellContext<WorkOrdersQueryListItem, any>) {
  "use no memo";
  const search = table.options.meta?.search ?? "";
  return (
    <div className="text-gray-500">
      <Highlighter searchString={search}>
        {row.original.departureAddress.city ?? ""}
      </Highlighter>
    </div>
  );
}

function ToCell({ row, table }: CellContext<WorkOrdersQueryListItem, any>) {
  "use no memo";
  const search = table.options.meta?.search ?? "";
  return (
    <div className="text-gray-500">
      <Highlighter searchString={search}>
        {row.original.deliveryAddress.city ?? ""}
      </Highlighter>
    </div>
  );
}

function DriversCell({ row }: CellContext<WorkOrdersQueryListItem, any>) {
  "use no memo";
  if (row.original.assignments.length === 0) {
    return <div className="text-gray-500">--</div>;
  }

  return (
    <div className="isolate flex -space-x-1 overflow-hidden">
      {row.original.assignments.map((assignment) => {
        const profilePictureUrl =
          assignment.proDriver.user?.profilePicture?.url;
        return (
          <Tooltip key={assignment.id}>
            <TooltipTrigger>
              <Avatar
                className="size-8 bg-gray-200 outline -outline-offset-1 outline-black/5 ring-2 ring-white"
                src={profilePictureUrl ?? undefined}
                initials={
                  profilePictureUrl
                    ? undefined
                    : getInitials(assignment.proDriver.name)
                }
              />
            </TooltipTrigger>
            <TooltipContent>{assignment.proDriver.name}</TooltipContent>
          </Tooltip>
        );
      })}
    </div>
  );
}
